import React, { useMemo } from 'react';
import { FiberManualRecord, WarningAmber } from '@mui/icons-material';
import { Tooltip, TooltipProps } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  breakpointAnalysisUrl,
  captableUrl,
  cashFlowLedgerUrl,
  fundOwnershipUrl,
} from 'common/config/urls';
import {
  BREAKPOINT_ANALYSIS_SLUG,
  BREAKPOINT_ANALYSIS_TITLE,
  CAP_TABLE_SLUG,
  CAP_TABLE_TITLE,
  CASH_FLOW_LEDGER_SLUG,
  CASH_FLOW_LEDGER_TITLE,
  FUND_OWNERSHIP_SLUG,
  FUND_OWNERSHIP_TITLE,
} from 'common/constants/cap-table';
import { ENTITY_QUERY_PARAM } from 'common/constants/pageFilters';
import { useStore } from 'common/store';
import { CapTableVersion } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import type { TertiaryNavigationLinkItem } from 'context/FirmBaseLayoutContext';

const NavTooltip = Tooltip as React.ComponentType<Omit<TooltipProps, 'children'>>;

const useCapTableTertiaryNavItems = (
  firmSlug: string,
  companySlug: string,
  capTableVersionsByCompany?: Record<string, CapTableVersion[]>
): TertiaryNavigationLinkItem[] => {
  const { search } = useLocation();
  const [{ captableInfo, companyInfo }] = useStore() as unknown as UseStoreValues;

  const relatedEntitiesWithCustomBreakpoints = useMemo(() => {
    if (!capTableVersionsByCompany || !companyInfo?.name) {
      return [];
    }
    const result: string[] = [];
    Object.entries(capTableVersionsByCompany).forEach(([compName, versions]) => {
      if (compName !== companyInfo.name && versions.some(v => v.use_custom_breakpoints)) {
        result.push(compName);
      }
    });
    return result;
  }, [capTableVersionsByCompany, companyInfo?.name]);

  const breakpointLabel = useMemo((): React.ReactNode => {
    if (captableInfo?.has_unconfigured_custom_breakpoints) {
      return React.createElement(
        React.Fragment,
        null,
        BREAKPOINT_ANALYSIS_TITLE,
        React.createElement(
          NavTooltip,
          {
            title: 'Some securities are missing values in the custom breakpoints configuration',
          },
          React.createElement(
            'span',
            { 'data-testid': 'breakpoint-analysis-nav-warning' },
            React.createElement(WarningAmber, {
              fontSize: 'small',
              color: 'warning',
              sx: { ml: 0.5 },
            })
          )
        )
      );
    }

    const hasPrimaryCustomBreakpoints = Boolean(captableInfo?.use_custom_breakpoints);
    const hasRelatedCustomBreakpoints = relatedEntitiesWithCustomBreakpoints.length > 0;

    if (hasPrimaryCustomBreakpoints && hasRelatedCustomBreakpoints) {
      const tooltipTitle = `Custom breakpoints are active (Primary & ${relatedEntitiesWithCustomBreakpoints.join(', ')})`;
      return React.createElement(
        React.Fragment,
        null,
        BREAKPOINT_ANALYSIS_TITLE,
        React.createElement(
          NavTooltip,
          {
            title: tooltipTitle,
          },
          React.createElement(
            'span',
            { 'data-testid': 'breakpoint-analysis-nav-indicator' },
            React.createElement(FiberManualRecord, {
              sx: { ml: 0.5, fontSize: 15, color: 'success.main' },
            })
          )
        )
      );
    }

    if (hasPrimaryCustomBreakpoints) {
      return React.createElement(
        React.Fragment,
        null,
        BREAKPOINT_ANALYSIS_TITLE,
        React.createElement(
          NavTooltip,
          {
            title: 'Custom breakpoints are active',
          },
          React.createElement(
            'span',
            { 'data-testid': 'breakpoint-analysis-nav-indicator' },
            React.createElement(FiberManualRecord, {
              sx: { ml: 0.5, fontSize: 15, color: 'success.main' },
            })
          )
        )
      );
    }

    if (hasRelatedCustomBreakpoints) {
      const tooltipTitle = `Custom breakpoints are active on related entity: ${relatedEntitiesWithCustomBreakpoints.join(', ')}`;
      return React.createElement(
        React.Fragment,
        null,
        BREAKPOINT_ANALYSIS_TITLE,
        React.createElement(
          NavTooltip,
          {
            title: tooltipTitle,
          },
          React.createElement(
            'span',
            { 'data-testid': 'breakpoint-analysis-nav-indicator-related' },
            React.createElement(FiberManualRecord, {
              sx: { ml: 0.5, fontSize: 15, color: 'info.main' },
            })
          )
        )
      );
    }

    return BREAKPOINT_ANALYSIS_TITLE;
  }, [
    captableInfo?.has_unconfigured_custom_breakpoints,
    captableInfo?.use_custom_breakpoints,
    relatedEntitiesWithCustomBreakpoints,
  ]);

  return useMemo<TertiaryNavigationLinkItem[]>(() => {
    // The entity filter belongs to the Cash Flow Ledger, so the other tabs drop it.
    const params = new URLSearchParams(search);
    params.delete(ENTITY_QUERY_PARAM);
    const otherTabSearch = params.toString() ? `?${params}` : '';
    return [
      { value: CAP_TABLE_SLUG, label: CAP_TABLE_TITLE, to: `${captableUrl(firmSlug, companySlug)}${otherTabSearch}` },
      {
        value: FUND_OWNERSHIP_SLUG,
        label: FUND_OWNERSHIP_TITLE,
        to: `${fundOwnershipUrl(firmSlug, companySlug)}${otherTabSearch}`,
      },
      {
        value: BREAKPOINT_ANALYSIS_SLUG,
        label: breakpointLabel,
        to: `${breakpointAnalysisUrl(firmSlug, companySlug)}${otherTabSearch}`,
      },
      { value: CASH_FLOW_LEDGER_SLUG, label: CASH_FLOW_LEDGER_TITLE, to: `${cashFlowLedgerUrl(firmSlug, companySlug)}${search}` },
    ];
  }, [firmSlug, companySlug, breakpointLabel, search]);
};

export default useCapTableTertiaryNavItems;
